import { useReveal } from '@/hooks/useReveal';

const problems = [
  {
    icon: 'ri-timer-line',
    title: 'Late Replies Cost You Customers',
    emotion: 'Frustration',
    description: '82% of customers expect a response within 5 minutes on WhatsApp. Every minute of delay costs you 7% in conversion rate. Your competitors are replying faster.',
    impact: 'Lost Revenue',
    color: 'from-red-500/20 to-orange-500/10',
    iconColor: 'text-red-400',
  },
  {
    icon: 'ri-user-unfollow-line',
    title: 'Manual Follow-ups Are Breaking Your Team',
    emotion: 'Exhaustion',
    description: 'Your sales team spends 60% of their day on repetitive follow-up messages instead of closing deals. Burnout is real, and your best people are leaving.',
    impact: 'Team Burnout',
    color: 'from-orange-500/20 to-amber-500/10',
    iconColor: 'text-orange-400',
  },
  {
    icon: 'ri-close-circle-line',
    title: 'Missed Leads = Missed Growth',
    emotion: 'Regret',
    description: 'The average business loses 45% of incoming WhatsApp leads due to no response or slow follow-up. These are customers who wanted to buy from you.',
    impact: 'Lost Opportunities',
    color: 'from-rose-500/20 to-pink-500/10',
    iconColor: 'text-rose-400',
  },
  {
    icon: 'ri-calendar-close-line',
    title: 'Missed Appointments Drain Revenue',
    emotion: 'Chaos',
    description: 'No-shows cost service businesses 15-20% of daily revenue. Without automated reminders, your calendar is a guessing game.',
    impact: 'Revenue Leak',
    color: 'from-purple-500/20 to-violet-500/10',
    iconColor: 'text-purple-400',
  },
  {
    icon: 'ri-emotion-unhappy-line',
    title: 'Poor Customer Experience Kills Loyalty',
    emotion: 'Disappointment',
    description: 'Customers who don\'t get instant responses feel ignored. 68% will never come back after one bad experience. Your reputation is at stake.',
    impact: 'Churn Risk',
    color: 'from-cyan-500/20 to-teal-500/10',
    iconColor: 'text-cyan-400',
  },
  {
    icon: 'ri-dislike-line',
    title: 'No CRM Means No Visibility',
    emotion: 'Blindness',
    description: 'Running your business on WhatsApp without a CRM is like driving blindfolded. You have no idea which conversations turn into sales or where leads drop off.',
    impact: 'No Insights',
    color: 'from-slate-500/20 to-gray-500/10',
    iconColor: 'text-slate-400',
  },
];

export default function ProblemsSection() {
  const { ref, visible } = useReveal();

  return (
    <section className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
      {/* Background subtle pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, oklch(var(--foreground-900)) 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
      </div>

      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-6 reveal ${visible ? 'visible' : ''}`}>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold uppercase tracking-wider mb-5">
            The Hidden Cost of Manual Communication
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-5 leading-tight">
            Your Business Is{' '}
            <span className="relative">
              Losing Money
              <span className="absolute -bottom-1 left-0 right-0 h-1 bg-rose-400/30 rounded-full"></span>
            </span>
            {' '}Right Now
          </h2>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            Every unanswered WhatsApp message, every delayed reply, every forgotten follow-up — it all adds up.
            Here&apos;s what it&apos;s really costing your business.
          </p>
        </div>

        {/* Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {problems.map((problem, i) => (
            <div
              key={problem.title}
              className="group relative bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-7 hover:border-rose-200/50 transition-all duration-500 hover:-translate-y-1"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Gradient accent top */}
              <div className={`absolute top-0 left-6 right-6 h-px bg-gradient-to-r ${problem.color.replace('20', '40')} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

              {/* Icon */}
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${problem.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-400`}>
                <i className={`${problem.icon} text-xl ${problem.iconColor}`}></i>
              </div>

              {/* Emotion tag */}
              <span className="inline-block px-2.5 py-1 rounded-md bg-rose-50 text-rose-600 text-[11px] font-semibold uppercase tracking-wider mb-3">
                {problem.emotion}
              </span>

              <h3 className="text-lg font-bold font-heading text-foreground-950 mb-2.5">
                {problem.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                {problem.description}
              </p>

              {/* Impact tag */}
              <div className="flex items-center gap-2 text-xs font-semibold text-rose-500">
                <i className="ri-error-warning-line"></i>
                {problem.impact}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}